import { Component, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AscReportService } from '../../services/asc-report.service';


@Component({
  selector: 'app-asc-transfer',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './asc-transfer.html',
  styleUrls: ['./asc-transfer.scss']
})
export class AscTransferComponent implements OnInit, OnChanges {
  @Input() odchgId: number | null = null;
  @Input() autoLoadRequestId: string | null = null;  // 自動ロードするRequestID
  
  // ナビゲーション用イベントエミッター
  @Output() navigateToJobOrder = new EventEmitter<number>();
  @Output() navigateToCustomer = new EventEmitter<number>();
  @Output() navigateToTs = new EventEmitter<{ timesheetId?: number }>();

  isLoading = false;
  errorMessage = '';
  moveTransferData: any = null;
  sourceData: any = null;  // 検索で取得した削除データ
  destinationTimesheetData: any = null;
  inputJobOrderId = '';

  // 削除データ検索用
  searchRequestId = '';
  searchOdchgId = '';
  searchSourceJobOrderId = '';
  
  // 検索で使用した実際の値（処理確定用）
  actualRequestId: string | null = null;  // 数字のみ（R を除く）
  actualOdchgId: number | null = null;    // 数字のみ（A を除く）
  
  // 検索で使用した転記元の獲注ID（転記先検索で使用）
  sourceJobOrderIdFromSearch: number | null = null;

  // 選択状態
  selectAllLineItems = false;
  selectedLineItems = new Set<number>();
  selectedCommentTimesheetIds = new Set<number>();

  // 転記先 DayDate フィルター適用後の表示用行リスト
  filteredSourceLineItems: any[] = [];

  // 状態フィルター
  filterHold = true;       // 保留
  filterReported = true;   // 報告済
  filterDenied = true;     // 否認
  filterReported2 = true;  // 承認済（CurrentStateID=40）

  constructor(
    private ascReportService: AscReportService
  ) {}

  ngOnInit() {
    // autoLoadRequestIdが設定されている場合、自動的に検索を実行
    if (this.autoLoadRequestId) {
      this.triggerAutoLoad(this.autoLoadRequestId);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // autoLoadRequestId が変化したとき（外部タブからの遷移など）に再検索
    const change = changes['autoLoadRequestId'];
    if (change && !change.firstChange && change.currentValue) {
      this.triggerAutoLoad(change.currentValue);
    }

    // odchgId が変化したとき（アラート期間タブの AID リンクからの遷移）
    const odchgChange = changes['odchgId'];
    if (odchgChange && odchgChange.currentValue != null) {
      this.triggerAutoLoadByOdchgId(odchgChange.currentValue);
    }
  }

  private triggerAutoLoad(requestId: string): void {
    this.clearSearch();
    this.searchRequestId = requestId;
    setTimeout(() => {
      this.loadSourceDataBySearch();
    }, 100);
  }

  private triggerAutoLoadByOdchgId(odchgId: number): void {
    this.clearSearch();
    this.searchOdchgId = 'A' + odchgId;
    setTimeout(() => {
      this.loadSourceDataBySearch();
    }, 100);
  }

  /**
   * 転記処理データを取得（ODchgIDで）
   */
  loadMoveTransferData(odchgId: number): void {
    this.isLoading = true;
    this.ascReportService.getMoveTransferData(odchgId).subscribe({
      next: (response) => {
        // ResponseDto構造の場合、dataプロパティから取得
        if (response && response.data) {
          this.moveTransferData = response.data;

          // コメントがあれば全選択で初期化
          if (this.moveTransferData?.sourceData?.comments && this.moveTransferData.sourceData.comments.length > 0) {
              this.selectedCommentTimesheetIds = new Set<number>(
              this.moveTransferData.sourceData.comments.map((c: any) => c.timesheetID)
            );
          } else {
            this.selectedCommentTimesheetIds.clear();
          }

        } else {
          this.moveTransferData = response;
        }
        
        this.isLoading = false;
      },
      error: (error) => {
        console.error('転記処理データ取得エラー:', error);
        this.errorMessage = 'データの取得に失敗しました';
        this.isLoading = false;
        this.moveTransferData = null;
      }
    });
  }

  /**
   * 状態フィルター変更時の処理
   */
  onStateFilterChange(): void {
    if (!this.filteredSourceLineItems.length) {
      return;
    }

    // 表示中の行についてのみフィルター条件を適用
    this.filteredSourceLineItems.forEach((item: any) => {
      const state = item.state || '';
      const shouldBeChecked = 
        (this.filterHold && state.includes('保')) ||
        (this.filterReported && state.includes('報')) ||
        (this.filterDenied && state.includes('否')) ||
        (this.filterReported2 && state.includes('認'));

      const itemId = item.timesheetLineItemID;
      if (shouldBeChecked) {
        this.selectedLineItems.add(itemId);
      } else {
        this.selectedLineItems.delete(itemId);
      }
    });

    // 全選択チェックボックスの状態を更新
    this.updateSelectAllState();
  }

  /**
   * 全選択チェックボックスの状態を更新
   */
  updateSelectAllState(): void {
    const totalItems = this.filteredSourceLineItems.length;
    this.selectAllLineItems = totalItems > 0 && this.selectedLineItems.size === totalItems;
  }

  /**
   * TimesheetLineItem全選択/解除
   */
  toggleSelectAllLineItems(): void {
    this.selectAllLineItems = !this.selectAllLineItems;
    
    if (this.selectAllLineItems) {
      // 表示中の行を全て選択
      this.filteredSourceLineItems.forEach((item: any) => {
        this.selectedLineItems.add(item.timesheetLineItemID);
      });
    } else {
      // 全て解除
      this.selectedLineItems.clear();
    }
  }

  /**
   * 個別チェックボックスクリック時
   */
  toggleLineItem(id: number): void {
    if (this.selectedLineItems.has(id)) {
      this.selectedLineItems.delete(id);
    } else {
      this.selectedLineItems.add(id);
    }
    
    // 全選択状態の更新（表示中の行数を基準にする）
    const totalCount = this.filteredSourceLineItems.length;
    this.selectAllLineItems = this.selectedLineItems.size === totalCount && totalCount > 0;
  }

  /**
   * 転記先の DayDate に一致する転記元行だけに絞り込む
   * 転記先未ロード時は全行表示
   */
  private applyDestinationFilter(): void {
    const allItems: any[] = this.moveTransferData?.sourceData?.lineItems || [];

    if (!this.destinationTimesheetData?.lineItems?.length) {
      // 転記先未ロード：全行表示
      this.filteredSourceLineItems = [...allItems];
    } else {
      // 転記先の DayDate セットを構築
      const destDates = new Set<string>(
        this.destinationTimesheetData.lineItems
          .map((d: any) => this.normalizeDayDate(d.dayDate))
          .filter((d: string) => !!d)
      );
      // 転記元行を絞り込み
      this.filteredSourceLineItems = allItems.filter((item: any) =>
        destDates.has(this.normalizeDayDate(item.dayDate))
      );
    }

    // 非表示になった行の選択を解除
    const visibleIds = new Set<number>(this.filteredSourceLineItems.map((i: any) => i.timesheetLineItemID));
    for (const id of [...this.selectedLineItems]) {
      if (!visibleIds.has(id)) {
        this.selectedLineItems.delete(id);
      }
    }

    this.updateSelectAllState();
  }

  /**
   * DayDate 文字列を YYYY-MM-DD に正規化（比較用）
   */
  private normalizeDayDate(dayDate: string | null | undefined): string {
    if (!dayDate) return '';
    const d = new Date(dayDate);
    if (isNaN(d.getTime())) return dayDate;
    const y = d.getFullYear();
    const m = (d.getMonth() + 1).toString().padStart(2, '0');
    const day = d.getDate().toString().padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  /**
   * TimesheetLineItemが選択されているか
   */
  isLineItemSelected(id: number): boolean {
    return this.selectedLineItems.has(id);
  }

  /**
   * Timesheetコメントチェックボックス変更
   */
  toggleCommentTimesheet(timesheetId: number): void {
    if (this.selectedCommentTimesheetIds.has(timesheetId)) {
      this.selectedCommentTimesheetIds.delete(timesheetId);
    } else {
      this.selectedCommentTimesheetIds.add(timesheetId);
    }
  }

  /**
   * コメントTimesheetが選択されているか
   */
  isCommentTimesheetSelected(timesheetId: number): boolean {
    return this.selectedCommentTimesheetIds.has(timesheetId);
  }

  /**
   * WeekEndingをyyyyMM形式でフォーマット
   */
  formatWeekEndingToYearMonth(weekEnding: string): string {
    if (!weekEnding) return '';
    const date = new Date(weekEnding);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    return `${year}${month}`;
  }

  /**
   * 開始・終了時間のフォーマット（0:00 または NULL の場合は空欄）
   */
  formatTime(time: string | null | undefined): string {
    if (!time) return '';
    const trimmed = time.trim();
    if (trimmed === '0:00' || trimmed === '00:00' || trimmed === '0') return '';
    return trimmed;
  }

  /**
   * 削除データの検索（RequestID, OdchgID, JobOrderIDで）
   */
  loadSourceDataBySearch(): void {
    // どれか1つでも入力されているか確認
    if (!this.searchRequestId && !this.searchOdchgId && !this.searchSourceJobOrderId) {
      alert('申請番号、アラート番号、または獲注IDのいずれかを入力してください。');
      return;
    }

    // 申請番号の検証と変換
    let requestIdValue: string | undefined = undefined;
    if (this.searchRequestId) {
      const trimmedRequestId = this.searchRequestId.trim();
      if (!trimmedRequestId.toUpperCase().startsWith('R')) {
        alert('申請番号は「R」で始まる必要があります（例: R12）。');
        return;
      }
      requestIdValue = trimmedRequestId.substring(1); // 先頭のRを削除
      if (!requestIdValue || requestIdValue.length === 0) {
        alert('申請番号が正しくありません。「R」の後に数字を入力してください（例: R12）。');
        return;
      }
    }

    // アラート番号の検証と変換
    let odchgIdValue: number | undefined = undefined;
    if (this.searchOdchgId) {
      const trimmedOdchgId = this.searchOdchgId.trim();
      if (!trimmedOdchgId.toUpperCase().startsWith('A')) {
        alert('アラート番号は「A」で始まる必要があります（例: A151）。');
        return;
      }
      const odchgIdStr = trimmedOdchgId.substring(1); // 先頭のAを削除
      if (!odchgIdStr || odchgIdStr.length === 0) {
        alert('アラート番号が正しくありません。「A」の後に数字を入力してください（例: A151）。');
        return;
      }
      odchgIdValue = Number(odchgIdStr);
      if (isNaN(odchgIdValue)) {
        alert('アラート番号の形式が正しくありません。「A」の後に数字を入力してください（例: A151）。');
        return;
      }
    }

    // 獲注IDの検証
    let jobOrderIdValue: number | undefined = undefined;
    if (this.searchSourceJobOrderId) {
      jobOrderIdValue = Number(this.searchSourceJobOrderId);
      if (isNaN(jobOrderIdValue)) {
        alert('獲注IDは数字で入力してください。');
        return;
      }
    }

    // 転記先・転記元表示を即座にリセット（初期状態に戻す）
    this.destinationTimesheetData = null;
    this.inputJobOrderId = '';
    this.filteredSourceLineItems = [];
    this.selectedLineItems.clear();
    this.selectedCommentTimesheetIds.clear();

    this.isLoading = true;
    this.errorMessage = '';

    // 新しいエンドポイント /api/AscReport/search-deleted-data を呼び出し
    this.ascReportService.searchDeletedData(
      requestIdValue,
      odchgIdValue,
      jobOrderIdValue
    ).subscribe({
      next: (response) => {
        if (response && response.data) {
          this.sourceData = response.data;
          
          // 検索で使用した実際の値を保存（処理確定用）
          this.actualRequestId = requestIdValue ? requestIdValue : (!odchgIdValue && response.data.requestID ? String(response.data.requestID) : null);
          // OdchgID: 検索パラメータ > レスポンスデータ（獲注番号検索時はレスポンスから取得）
          this.actualOdchgId = odchgIdValue !== undefined ? odchgIdValue : (response.data.odchgID || null);
          

          // JobOrderIDを取得（優先順位: 検索パラメータ > レスポンスデータ）
          if (jobOrderIdValue) {
            this.sourceJobOrderIdFromSearch = jobOrderIdValue;
          } else if (response.data.jobOrderID) {
            this.sourceJobOrderIdFromSearch = response.data.jobOrderID;
          } else {
            console.warn('JobOrderIDが取得できませんでした');
          }
          
          // moveTransferDataにも設定して、既存のHTMLテンプレートで表示できるようにする
          this.moveTransferData = {
            sourceData: response.data,
            summary: {
              jobOrderID: this.sourceJobOrderIdFromSearch || 0
            },
            destinations: [] // 検索の場合は転記先候補は空
          };
          this.isLoading = false;
  
          // コメントがあれば全選択で初期化（テーブル描画時にチェック済みにする）
          if (this.moveTransferData?.sourceData?.comments && this.moveTransferData.sourceData.comments.length > 0) {
              this.selectedCommentTimesheetIds = new Set<number>(
              this.moveTransferData.sourceData.comments.map((c: any) => c.timesheetID)
            );
          } else {
            this.selectedCommentTimesheetIds.clear();
          }

          // フィルターを初期状態（全チェック）に戻してからフィルター適用
          this.filterHold = true;
          this.filterReported = true;
          this.filterDenied = true;
          this.filterReported2 = true;
          this.selectedLineItems.clear();
          // 転記先フィルターを適用してから状態フィルターを適用
          this.applyDestinationFilter();
          this.onStateFilterChange();
        } else {
          alert('データが見つかりませんでした。');
          this.isLoading = false;
        }
      },
      error: (error) => {
        console.error('削除データ取得エラー:', error);
        const errorMsg = error?.error?.message || error?.message || '削除データの取得に失敗しました。';
        alert(`エラー: ${errorMsg}`);
        this.isLoading = false;
      }
    });
  }

  /* 削除: sendNoDestinationEmail - 要件5で削除 */
  /* 削除: deleteFromSource - 要件8で削除 */

  /**
   * 転記処理（選択された明細を転記先に転記）
   */
  moveToDestination(): void {
    // 確認ダイアログを表示
    const confirmed = confirm('選択された明細を転記先に転記します。よろしいですか？');
    
    if (!confirmed) {
      return;
    }

    // 選択されたTimesheetLineItemIDsを配列として取得
    const selectedLineItemIds = Array.from(this.selectedLineItems);
    const selectedCommentIds = Array.from(this.selectedCommentTimesheetIds);
    
    if (selectedLineItemIds.length === 0 && selectedCommentIds.length === 0) {
      alert('転記する明細またはコメントを選択してください。');
      return;
    }
    
    // 転記先タイムシートが表示されていない場合はエラー
    if (!this.destinationTimesheetData || !this.destinationTimesheetData.jobOrderID) {
      alert('転記先タイムシートが表示されていません。転記先を選択してください。');
      return;
    }

    // 転記元キーの解決（検索フローでは RequestID / ODchgID / JobOrderID のいずれかを使用）
    const resolvedOdchgId = this.actualOdchgId ?? this.odchgId;
    const resolvedRequestId = this.actualRequestId ? Number(this.actualRequestId) : null;
    const resolvedSourceJobOrderId = this.sourceJobOrderIdFromSearch ?? this.moveTransferData?.summary?.jobOrderID ?? null;
    if (!resolvedOdchgId && !resolvedRequestId && !resolvedSourceJobOrderId) {
      alert('転記元を特定するIDが取得できません。再度検索してください。');
      return;
    }

    // UserIDの取得
    const currentUserIdStr = sessionStorage.getItem('UserID');
    const currentUserId = currentUserIdStr ? parseInt(currentUserIdStr) : 0;

    const request = {
      ODchgID: resolvedOdchgId,
      RequestID: resolvedRequestId,
      SourceJobOrderID: resolvedSourceJobOrderId,
      NewJobOrderID: this.destinationTimesheetData.jobOrderID,
      SelectedLineItemIDs: selectedLineItemIds,
      SelectedCommentTimesheetIDs: selectedCommentIds,
      CurrentUserID: currentUserId
    };

    this.isLoading = true;
    this.ascReportService.moveToDestination(request).subscribe({
      next: (response) => {
        
        if (response && response.status) {
          // 転記先タイムシートを再読み込み（転記元の獲注IDを使用）
          const sourceJobOrderId = this.sourceJobOrderIdFromSearch ?? this.moveTransferData?.summary?.jobOrderID;
          if (sourceJobOrderId && this.destinationTimesheetData?.jobOrderID) {
            this.loadDestinationTimesheet(sourceJobOrderId, this.destinationTimesheetData.jobOrderID);
          }
          
          // TODO: 「アラート（勤務日）」タブの表を更新
          
        } else {
          alert('転記処理に失敗しました。');
        }
        
        this.isLoading = false;
      },
      error: (error) => {
        console.error('転記処理エラー:', error);
        alert('転記処理中にエラーが発生しました。');
        this.isLoading = false;
      }
    });
  }

  /**
   * 転記/削除を完了する（旧版・互換性のため残す）
   */
  completeMoveOrDelete(): void {
    // 確認ダイアログを表示
    const confirmed = confirm('既存データを転記し、現タイムシートテーブルから削除します。よろしいですか？');
    
    if (!confirmed) {
      return;
    }

    // 選択されたTimesheetLineItemIDsを配列として取得
    const selectedLineItemIds = Array.from(this.selectedLineItems);
    
    // 転記先タイムシートが表示されていない場合はエラー
    if (!this.destinationTimesheetData || !this.destinationTimesheetData.jobOrderID) {
      alert('転記先タイムシートが表示されていません。転記先を選択してください。');
      return;
    }

    // 必要なデータが揃っているか確認
    if (!this.odchgId || !this.moveTransferData?.summary?.jobOrderID) {
      alert('必要なデータが不足しています。');
      return;
    }

    const request = {
      ODchgID: this.odchgId,
      OldJobOrderID: this.moveTransferData.summary.jobOrderID,
      NewJobOrderID: this.destinationTimesheetData.jobOrderID,
      SelectedLineItemIDs: selectedLineItemIds,
      SelectedCommentTimesheetIDs: Array.from(this.selectedCommentTimesheetIds)
    };

    this.isLoading = true;
    this.ascReportService.completeMoveOrDelete(request).subscribe({
      next: (response) => {
        if (response && response.status) {
          alert('転記/削除処理が完了しました。');
          
          // 転記先タイムシートを再読み込み
          if (this.moveTransferData?.summary?.jobOrderID && this.destinationTimesheetData?.jobOrderID) {
            this.loadDestinationTimesheet(this.moveTransferData.summary.jobOrderID, this.destinationTimesheetData.jobOrderID);
          }
          
          // TODO: 「アラート（勤務日）」タブの表を更新
          // 親コンポーネントにイベントを通知する必要がある場合は、@Output()を使用
          
        } else {
          alert('転記/削除処理に失敗しました。');
        }
        
        this.isLoading = false;
      },
      error: (error) => {
        console.error('転記/削除処理エラー:', error);
        alert('転記/削除処理中にエラーが発生しました。');
        this.isLoading = false;
      }
    });
  }

  /**
   * 転記先タイムシートデータを取得（リンククリック時）
   */
  loadDestinationTimesheetByLink(jobOrderId: number): void {
    // 転記元データが読み込まれているか確認
    if (!this.moveTransferData || !this.moveTransferData.summary) {
      alert('転記元データが読み込まれていません。');
      return;
    }

    // 転記元のJobOrderIDを取得
    const sourceJobOrderId = this.moveTransferData.summary.jobOrderID;
    if (!sourceJobOrderId) {
      alert('転記元の獲注IDが取得できません。');
      return;
    }

    this.loadDestinationTimesheet(sourceJobOrderId, jobOrderId);
  }

  /**
   * 転記先タイムシートデータを取得（入力+ボタン押下時）
   */
  loadDestinationTimesheetByInput(): void {
    // 転記元データが読み込まれているか確認
    if (!this.moveTransferData) {
      alert('先に転記元データを検索してください。');
      return;
    }

    if (!this.moveTransferData.summary || !this.moveTransferData.summary.jobOrderID) {
      alert('転記元の獲注IDが取得できません。転記元データを再検索してください。');
      return;
    }

    const jobOrderId = parseInt(this.inputJobOrderId, 10);
    if (isNaN(jobOrderId) || jobOrderId <= 0) {
      alert('有効な獲注IDを入力してください');
      return;
    }

    // 転記元のJobOrderIDを取得
    const sourceJobOrderId = this.moveTransferData.summary.jobOrderID;
    
    this.loadDestinationTimesheet(sourceJobOrderId, jobOrderId);
  }

  /**
   * 転記先タイムシートデータ取得の共通処理
   */
  private loadDestinationTimesheet(sourceJobOrderId: number, destinationJobOrderId: number): void {
    this.isLoading = true;
    this.ascReportService.getDestinationTimesheetData(sourceJobOrderId, destinationJobOrderId).subscribe({
      next: (response) => {
        if (response.status && response.data) {
          this.destinationTimesheetData = response.data;
          // 転記先の DayDate に均って転記元行を絞り込む
          this.applyDestinationFilter();
        } else {
          this.errorMessage = response.message || '転記先データの取得に失敗しました';
          alert(this.errorMessage);
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('転記先タイムシートデータ取得エラー:', error);
        // StaffID不一致エラーの場合
        if (error.status === 400 && error.error?.message === 'この獲注のASCが異なります。') {
          alert('この獲注のASCが異なります。');
        } else {
          this.errorMessage = error.error?.message || '転記先データの取得に失敗しました';
          alert(this.errorMessage);
        }
        this.isLoading = false;
      }
    });
  }

  /**
   * 日付範囲文字列をフォーマット (yyyy-MM-dd～yyyy-MM-dd → yyyy/MM/dd～yyyy/MM/dd)
   */
  formatDateRange(dateRange: string | undefined): string {
    if (!dateRange) return '';
    
    // 日付範囲を～で分割
    const parts = dateRange.split('～');
    if (parts.length !== 2) return dateRange;
    
    // 各日付のハイフンをスラッシュに置換
    const formattedStart = parts[0].trim().replace(/-/g, '/');
    const formattedEnd = parts[1].trim().replace(/-/g, '/');
    
    return `${formattedStart}～${formattedEnd}`;
  }

  /**
   * Manager TimesheetページへのURLを生成（暗号化版）
   * @param timesheetId TimesheetID
   * @param staffId StaffID (UserIDとして使用)
   * @returns Manager TimesheetページのURL
   */
  onTimesheetClick(timesheetId: number | undefined): void {
    if (!timesheetId) return;
    this.navigateToTs.emit({ timesheetId });
  }

  /**
   * 獲注情報タブを開く
   * @param jobOrderId 獲注ID
   */
  openJobOrderInfo(jobOrderId: number | undefined): void {
    if (jobOrderId) {
      this.navigateToJobOrder.emit(jobOrderId);
    }
  }

  /**
   * 顧客情報タブを開く
   * @param customerOfficeId 顧客部署ID
   */
  openCustomerInfo(customerOfficeId: number | undefined): void {
    if (customerOfficeId) {
      this.navigateToCustomer.emit(customerOfficeId);
    }
  }

  /**
   * 処理を確定する
   */
  confirmProcessing(): void {
    // 確認ダイアログ
    if (!confirm('処理を確定しますか？')) {
      return;
    }

    this.isLoading = true;

    // 取得できているIDを両方送信（片方だけでも動作するようバックエンドが両対応）
    const request: any = {};
    if (this.actualRequestId) {
      request.RequestID = this.actualRequestId;
    }
    if (this.actualOdchgId) {
      request.ODchgID = this.actualOdchgId;
    }
    if (!request.RequestID && !request.ODchgID) {
      alert('申請番号またはアラート番号が見つかりません。');
      this.isLoading = false;
      return;
    }

    this.ascReportService.confirmProcess(request).subscribe({
      next: (response) => {
        if (response) {
          alert('処理が正常に確定されました。');
          // 必要に応じて画面をリフレッシュまたはクリア
          this.clearSearch();
        } else {
          alert('処理の確定に失敗しました。');
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('処理確定エラー:', error);
        const errorMsg = error?.error?.message || error?.message || '処理の確定中にエラーが発生しました。';
        alert(`エラー: ${errorMsg}`);
        this.isLoading = false;
      }
    });
  }

  /**
   * 検索状態をクリア
   */
  clearSearch(): void {
    this.searchRequestId = '';
    this.searchOdchgId = '';
    this.searchSourceJobOrderId = '';
    this.actualRequestId = null;
    this.actualOdchgId = null;
    this.sourceData = null;
    this.sourceJobOrderIdFromSearch = null;
    this.moveTransferData = null;
    this.destinationTimesheetData = null;
    this.inputJobOrderId = '';
    this.selectedLineItems.clear();
    this.selectedCommentTimesheetIds.clear();
    this.filteredSourceLineItems = [];
    this.filterHold = true;
    this.filterReported = true;
    this.filterDenied = true;
    this.filterReported2 = true;
  }}